import type { NormalizedCacheObject } from '@apollo/client/core';
import {
  ApolloClient,
  ApolloLink,
  HttpLink,
  InMemoryCache,
  concat
} from '@apollo/client/core';
import { loadDevMessages, loadErrorMessages } from '@apollo/client/dev';
import type { NormalizedConnections } from '@solfacil/plasma-utils';
import { useConnections, useEnvironment } from '@solfacil/plasma-utils';
import { provideApolloClients } from '@vue/apollo-composable';
import type { App } from 'vue';

export default defineNuxtPlugin(({ vueApp }) => {
  if (import.meta.dev) {
    loadDevMessages();
    loadErrorMessages();
  }

  const { graphql } = useConnections();

  const defaultConfig = generateDefaultConfig(graphql);
  const extraConfigs = generateExtraConfigs(graphql);

  if (!defaultConfig) { throw new Error('No default GraphQL connection found.'); }

  applyApolloConfig(vueApp, defaultConfig, extraConfigs);
});

function generateDefaultConfig (graphql: NormalizedConnections['graphql']): ApolloClient<NormalizedCacheObject> | undefined {
  const config = graphql.default;
  return config ? createApolloClient(config.url) : undefined;
}

function generateExtraConfigs (graphql: NormalizedConnections['graphql']): { [key: string]: ApolloClient<NormalizedCacheObject> } {
  const configs: { [key: string]: ApolloClient<NormalizedCacheObject> } = {};

  for (const key in graphql) {
    if (key === 'default') { continue; }
    const config = graphql[key];
    if (config) {
      const client = createApolloClient(config.url);
      Object.defineProperty(configs, key, { value: client });
    }
  }

  return configs;
}

function applyApolloConfig (_app: App, defaultClient: ApolloClient<NormalizedCacheObject>, clients: { [key: string]: ApolloClient<NormalizedCacheObject> }): void {
  // Object.assign(extraClients, { default: defaultClient });
  // _app.provide(ApolloClients, extraClients);
  provideApolloClients({ default: defaultClient, ...clients });
}

function createApolloClient (url: string): ApolloClient<NormalizedCacheObject> {
  const httpLink = new HttpLink({ uri: url });
  const client = new ApolloClient({
    link: concat(getAuthMiddleware(), httpLink),
    cache: new InMemoryCache(),
    ssrForceFetchDelay: 100
  });

  return client;
}

function getAuthMiddleware () {
  const accessToken = useCookie('access_token', {
    httpOnly: useEnvironment() === 'prod'
  });
  const authMiddleware = new ApolloLink((operation, forward) => {
    operation.setContext(({ headers = {} }) => ({
      headers: {
        ...headers,
        Authorization: accessToken.value ? `Bearer ${accessToken.value}` : null
      }
    }));
    return forward(operation);
  });

  return authMiddleware;
}
